import Groq from "groq-sdk";
import { toast } from "react-toastify";

// Define available languages
export const SUPPORTED_LANGUAGES = [
  "Български", // Bulgarian
  "Английски", // English
  "Немски", // German
] as const;

// Create a type from the array values
export type CampaignLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export interface SelectedProject {
  ["campaign-language"]: CampaignLanguage;
  event: string;
  ["marketing-platform"]: string;
  ["product-description"]: string;
  ["target-country"]: string;
  ["project"]: string;
  ["choose-client"]: string;
}

interface Avatar {
  data: string;
  interests: string;
  profile: string;
  values: string;
  needs: string;
  behavior: string;
  messages: string;
}

// Translation mappings for section headers
const TRANSLATIONS = {
  Български: {
    demographics: "Демография",
    interests: "Интереси и хобита",
    psychological: "Психологически профил",
    values: "Ценности",
    needs: "Нужди и проблеми",
    behavior: "Поведение при покупка",
    messages: "Ключови съобщения",
  },
  Английски: {
    demographics: "Demographics",
    interests: "Interests and Hobbies",
    psychological: "Psychological Profile",
    values: "Values",
    needs: "Needs and Problems",
    behavior: "Purchase Behavior",
    messages: "Key Messages",
  },
  Немски: {
    demographics: "Demografie",
    interests: "Interessen und Hobbys",
    psychological: "Psychologisches Profil",
    values: "Werte",
    needs: "Bedürfnisse und Probleme",
    behavior: "Kaufverhalten",
    messages: "Hauptbotschaften",
  },
} as const;

// Get section patterns based on language
const getSectionPatterns = (language: CampaignLanguage) => ({
  DEMOGRAPHICS: new RegExp(
    `\\*\\*${TRANSLATIONS[language].demographics}\\*\\*`,
    "i"
  ),
  INTERESTS: new RegExp(`\\*\\*${TRANSLATIONS[language].interests}\\*\\*`, "i"),
  PSYCHOLOGICAL: new RegExp(
    `\\*\\*${TRANSLATIONS[language].psychological}\\*\\*`,
    "i"
  ),
  VALUES: new RegExp(`\\*\\*${TRANSLATIONS[language].values}\\*\\*`, "i"),
  NEEDS: new RegExp(`\\*\\*${TRANSLATIONS[language].needs}\\*\\*`, "i"),
  BEHAVIOR: new RegExp(`\\*\\*${TRANSLATIONS[language].behavior}\\*\\*`, "i"),
  MESSAGES: new RegExp(`\\*\\*${TRANSLATIONS[language].messages}\\*\\*`, "i"),
});

const extractSection = (
  content: string,
  startPattern: RegExp,
  endPattern?: RegExp
): string => {
  const startMatch = content.match(startPattern);
  if (!startMatch) return "";

  const startIndex = startMatch.index;
  if (startIndex === undefined) return "";

  let endIndex: number;
  if (endPattern) {
    const endMatch = content
      .slice(startIndex + startMatch[0].length)
      .match(endPattern);
    endIndex =
      endMatch && endMatch.index !== undefined
        ? startIndex + startMatch[0].length + endMatch.index
        : content.length;
  } else {
    endIndex = content.length;
  }

  const sectionContent = content
    .slice(startIndex + startMatch[0].length, endIndex)
    .split("\n")
    .filter((line) => line.trim().startsWith("*"))
    .map((line) =>
      line
        .replace(/^\*+\s*/, "")
        .replace(/\*+/g, "")
        .trim()
    )
    .filter(Boolean)
    .join(", ");

  return sectionContent;
};

const generatePrompt = (project: SelectedProject): string => {
  const translations = TRANSLATIONS[project["campaign-language"]];

  return `Generate a detailed customer avatar profile for:
- Platform: ${project["marketing-platform"]}
- Language: ${project["campaign-language"]}
- Product: ${project["product-description"]}
- Country: ${project["target-country"]}
- Event: ${project.event}

Please format the response with the following sections using markdown:
**${translations.demographics}**
[List demographic data]

**${translations.interests}**
[List interests and hobbies]

**${translations.psychological}**
[List psychological profile characteristics]

**${translations.values}**
[List core values]

**${translations.needs}**
[List key needs and problems]

**${translations.behavior}**
[List purchase behavior]

**${translations.messages}**
[List key messages]

Keep the total response under 500 characters and respond in ${project["campaign-language"]}.`;
};

const parseResponse = (content: string, language: CampaignLanguage): Avatar => {
  // MODIFY ...
  const extractSection = (
    content: string,
    startPattern: RegExp,
    endPattern?: RegExp
  ): string => {
    const startMatch = content.match(startPattern);
    if (!startMatch) return "";

    const startIndex = startMatch.index;
    if (startIndex === undefined) return "";

    let endIndex: number;
    if (endPattern) {
      const endMatch = content
        .slice(startIndex + startMatch[0].length)
        .match(endPattern);
      endIndex =
        endMatch?.index !== undefined
          ? startIndex + startMatch[0].length + endMatch.index
          : content.length;
    } else {
      endIndex = content.length;
    }

    const sectionContent = content
      .slice(startIndex + startMatch[0].length, endIndex)
      .trim()
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.startsWith("*") || line.startsWith("-"))
      .map((line) => line.replace(/^[*-]\s*/, "").trim())
      .filter(Boolean)
      .join(", ");

    return sectionContent;
  };
  // MODIFY ...

  const patterns = getSectionPatterns(language);
  const patternEntries = Object.entries(patterns);

  const sections: Record<string, string> = {};

  patternEntries.forEach(([key, pattern], index) => {
    const nextPattern = patternEntries[index + 1]?.[1];
    sections[key.toLowerCase()] = extractSection(content, pattern, nextPattern);
  });

  return {
    data: sections.demographics || "",
    interests: sections.interests || "",
    profile: sections.psychological || "",
    values: sections.values || "",
    needs: sections.needs || "",
    behavior: sections.behavior || "",
    messages: sections.messages || "",
  };
};

export const generateAvatar = async (
  project: SelectedProject
): Promise<Avatar> => {
  console.log("===== === ===: generateAvatar() func Triggered!");
  if (!process.env.REACT_APP_GROQ_API_KEY) {
    throw new Error("API key is not defined");
  }

  const groq = new Groq({
    apiKey: process.env.REACT_APP_GROQ_API_KEY,
    dangerouslyAllowBrowser: true,
  });

  try {
    const response = await groq.chat.completions.create({
      messages: [
        {
          role: "system",
          content: `You are an expert marketing strategist. Create detailed customer avatars using markdown formatting with bullet points. Keep responses concise and well-structured. Respond in ${project["campaign-language"]}.`,
        },
        {
          role: "user",
          content: generatePrompt(project),
        },
      ],
      model: "llama3-8b-8192",
      max_tokens: 500,
      temperature: 0.7,
    });

    const responseContent = response.choices[0]?.message?.content || "";
    console.log("======== api.ts =======, log: ", responseContent);
    return parseResponse(responseContent, project["campaign-language"]);
  } catch (error) {
    toast.error("Надвишен лимит! Моля, опитайте след 24 часа.");
    console.error("Error calling API:", error);
    return {
      data: "",
      interests: "",
      profile: "",
      values: "",
      needs: "",
      behavior: "",
      messages: "",
    };
  }
};


type Language = typeof SUPPORTED_LANGUAGES[number];
type Platform = 'Facebook' | 'Google' | 'Instagram';

interface PromptConfig {
  systemPrompt: string;
  formatInstructions: string;
  platformInstructions: Record<Platform, string>;
  grammarRules: string;
}

interface AdCopyValidation {
  maxTitleLength: number;
  maxDescriptionLength: number; 
  requiredElements: string[];
}

const platformValidation: Record<Platform, AdCopyValidation> = {
  Facebook: {
    maxTitleLength: 40,
    maxDescriptionLength: 125,
    // forbiddenCharacters: /[^\p{L}\p{N}\p{P}\p{Z}]/u,  // Only letters, numbers, punctuation, and spaces
    requiredElements: ['call-to-action']
  },
  Google: {
    maxTitleLength: 30,
    maxDescriptionLength: 90,
    // forbiddenCharacters: /[^\p{L}\p{N}\p{P}\p{Z}]/u,
    requiredElements: ['headline', 'description']
  },
  Instagram: {
    maxTitleLength: 125,
    maxDescriptionLength: 2200,
    // forbiddenCharacters: /[^\p{L}\p{N}\p{P}\p{Z}\p{S}]/u,  // Also allows emojis
    requiredElements: ['caption']
  }
};

const languagePrompts: Record<Language, PromptConfig> = {
  "Български": {
    systemPrompt: "Вие сте експерт копирайтър и езиков специалист. Създайте граматически правилен, въздействащ рекламен текст, който следва правилата на българския език. Важно: Не включвайте никакви мета-коментари или административни бележки. Не превеждайте имена на продукти, марки или специфични термини от английски език - оставете ги в оригинал. Например 'Apple iPhone', 'Microsoft Windows', 'Nike Air Max' остават непреведени. Предоставете директно само готовия рекламен текст.",
    formatInstructions: `
- Спазвайте правилата за пунктуация
- Използвайте правилен словоред
- Съгласувайте правилно прилагателните и съществителните
- Използвайте подходящи глаголни времена
- Максимум 500 знака`,
    platformInstructions: {
      Facebook: "- Разговорен, но граматически правилен тон\n- Емоционално въздействащ текст\n- Правилна пунктуация в края на изреченията\n- Ясен призив за действие",
      Google: "- Кратки, граматически верни заглавия\n- Правилно членуване\n- Точна пунктуация\n- Спазвайте ограниченията на Google Ads",
      Instagram: "- Граматически издържан текст\n- Правилна употреба на препинателни знаци\n- Умерена употреба на емоджита\n- Ясен призив за действие"
    },
    grammarRules: `
- Проверка за двойно отрицание
- Правилна употреба на пълен и кратък член
- Съгласуване по род и число
- Правилна употреба на запетаи при подчинени изречения
- Правилен словоред на частиците "се" и "ще"`
  },
  "Английски": {
    systemPrompt: "You are an expert copywriter and language specialist. Create grammatically correct, compelling ad copy that follows English language rules. Important: Do not include any meta-comments, administrative notes, or editing explanations. Do not use asterisks (*) or similar markers. Provide only the final ad copy directly.",
    formatInstructions: `
- Use proper punctuation
- Maintain subject-verb agreement
- Use correct verb tenses
- Apply proper article usage (a/an/the)
- Maximum 500 characters`,
    platformInstructions: {
      Facebook: "- Conversational yet grammatically correct tone\n- Emotionally engaging\n- Proper sentence endings\n- Clear call-to-action",
      Google: "- Concise, grammatically correct headlines\n- Proper capitalization\n- Accurate punctuation\n- Follow Google Ads limitations",
      Instagram: "- Grammatically sound copy\n- Proper punctuation usage\n- Strategic emoji placement\n- Clear call-to-action"
    },
    grammarRules: `
- Check for subject-verb agreement
- Proper use of articles and determiners
- Correct verb tense sequence
- Appropriate preposition usage
- Proper comma usage in compound sentences`
  },
  "Немски": {
    systemPrompt: "Sie sind ein erfahrener Texter und Sprachexperte. Erstellen Sie grammatikalisch korrekte, überzeugende Werbetexte, die den Regeln der deutschen Sprache folgen. Wichtig: Fügen Sie keine Meta-Kommentare oder administrative Hinweise hinzu. Behalten Sie Produkt-, Marken- und spezifische Begriffe in ihrer originalen englischen Form bei. Zum Beispiel bleiben 'Apple iPhone', 'Microsoft Windows', 'Nike Air Max' unübersetzt. Liefern Sie nur den fertigen Werbetext direkt.",
    formatInstructions: `
- Korrekte Interpunktion
- Richtige Wortstellung (V2, Verbklammer)
- Korrekte Deklination
- Richtige Zeitformen
- Maximal 500 Zeichen`,
    platformInstructions: {
      Facebook: "- Umgangssprachlich, aber grammatikalisch korrekt\n- Emotional ansprechend\n- Korrekte Satzzeichen\n- Klarer Call-to-Action",
      Google: "- Prägnante, grammatikalisch korrekte Headlines\n- Korrekte Großschreibung\n- Genaue Zeichensetzung\n- Google Ads Beschränkungen beachten",
      Instagram: "- Grammatikalisch einwandfreier Text\n- Korrekte Zeichensetzung\n- Strategischer Emoji-Einsatz\n- Klarer Call-to-Action"
    },
    grammarRules: `
- Korrekte Verbstellung im Haupt- und Nebensatz
- Richtige Artikel- und Adjektivdeklination
- Kasus nach Präpositionen
- Kommasetzung bei Nebensätzen
- Korrekte Verwendung der Zeitformen`
  }
};

const validateAdCopy = (
  text: string, 
  platform: Platform, 
  language: Language
): { isValid: boolean; errors: string[] } => {
  const validation = platformValidation[platform];
  const errors: string[] = [];

  // Check length constraints
  const lines = text.split('\n');
  const title = lines[0] || '';
  const description = lines.slice(1).join('\n');

  if (title.length > validation.maxTitleLength) {
    errors.push(`Title exceeds maximum length of ${validation.maxTitleLength} characters`);
  }

  if (description.length > validation.maxDescriptionLength) {
    errors.push(`Description exceeds maximum length of ${validation.maxDescriptionLength} characters`);
  }
 
  // Check for required elements
  for (const element of validation.requiredElements) {
    if (!text.toLowerCase().includes(element.toLowerCase())) {
      errors.push(`Missing required element: ${element}`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const generateAdText = async (
  project: SelectedProject,
): Promise<string> => {
  const apiKey = process.env.REACT_APP_GROQ_API_KEY;
  const groq = new Groq({
    apiKey,
    dangerouslyAllowBrowser: true,
  });
  
  const language = project["campaign-language"] as Language;
  const platform = project["marketing-platform"] as Platform;
  
  const promptConfig = languagePrompts[language];
  if (!promptConfig) {
    toast.error(`Unsupported language: ${language}`);
    throw new Error(`Unsupported language: ${language}`);
  }

  // Extract potential product names (words with capital letters or specific patterns)
const extractProductNames = (description: string): string[] => {
  const potentialNames = description.match(/[A-Z][a-zA-Z]*(?:\s+[A-Z][a-zA-Z]*)*/g) || [];
  return potentialNames.filter(name => name.length > 1); // Filter out single letters
};

const productNames = extractProductNames(project["product-description"]);
const preserveTerms = productNames.length 
  ? `\n\nPreserve these terms in their original English form: ${productNames.join(', ')}` 
  : '';

const prompt = `Create grammatically correct ad copy for ${project["product-description"]} targeting ${project["target-country"]} audience.${preserveTerms}

Key details:
- Platform: ${platform}
- Event: ${project.event}

Platform-specific requirements:
${promptConfig.platformInstructions[platform]}

Grammar rules to follow:
${promptConfig.grammarRules}

General requirements:
${promptConfig.formatInstructions}

return just the ad copy
`;

  try {
    const response = await groq.chat.completions.create({
      messages: [
        {
          role: "system",
          content: promptConfig.systemPrompt,
        },
        {
          role: "user",
          content: prompt,
        }
      ],
      model: "mixtral-8x7b-32768",
      temperature: 0.7,
      max_tokens: 500,
    });

    let adText = response.choices[0]?.message?.content?.trim() || "";
    
    // Remove meta-comments and administrative text
    adText = adText
      .replace(/\*.*?(публикация|съдържа|преработена).*?\*/g, '')
      .replace(/Тази публикация съдържа граматични грешки.*?действие\./g, '')
      .trim();
    
    // Validate the generated ad copy
    const validation = validateAdCopy(adText, platform, language);
    
    if (!validation.isValid) {
      console.warn('Ad copy validation failed:', validation.errors);
      // You might want to retry generation or adjust the text here
    }

    return adText;

  } catch (error) {
    const errorMessage = language === "Български" 
      ? "Надвишен лимит! Моля, опитайте след 24 часа."
      : language === "Немски"
      ? "Limit überschritten! Bitte versuchen Sie es in 24 Stunden erneut."
      : "Limit exceeded! Please try again in 24 hours.";
    
    toast.error(errorMessage);
    return "";
  }
};